import React, { useRef, useState } from 'react';
import css from '../OneWayInOpportunityHeader/OneWayInOpportunityHeader.module.scss';
import { Button, Icon } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import BrregCompanySearch, { BrregCompany } from 'src/components/BrregCompanySearch/BrregCompanySearch';
import useOneWayIn from 'src/features/one-way-in/hooks/use-one-way-in';
import { PageParams } from '../../types/PageParams';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ToastContent from 'src/components/ToastContent/ToastContent';
import useOneWayInFeedback from 'src/features/one-way-in/hooks/use-one-way-in-feedback';
import useFeedbackMissing from 'src/features/one-way-in/hooks/use-feedback-missing';
import { useUser } from 'src/features/authorization';
import createAutomaticFeedbackUpsertDto from 'src/features/one-way-in/utils/createAutomaticFeedbackUpsertDto';
import DialogActions from 'src/components/DialogActions/DialogActions';
import DialogContainer from 'src/components/DialogContainer/DialogContainer';

interface ConnectCompanyModalProps {
  show: boolean;
  onClose: () => void;
}

const ConnectCompanyModal: React.FC<ConnectCompanyModalProps> = ({ show, onClose }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const { t: tSiva } = useTranslation('siva');
  const { formId } = useParams<PageParams>();
  const { addMember, oneWayInOpportunityById } = useOneWayIn(formId);

  const [selectedCompany, setSelectedCompany] = useState<
    Pick<BrregCompany, 'organizationNumber' | 'name'> | undefined
  >(undefined);
  const { opportunityFeedbackCollection, upsertMutation: upsertFeedback } = useOneWayInFeedback(formId);
  const feedbackMissing = useFeedbackMissing(opportunityFeedbackCollection, oneWayInOpportunityById.data);
  const { chosenClusterId } = useUser();

  const onConnectCompany = () => {
    if (!selectedCompany?.organizationNumber || !oneWayInOpportunityById.data?.oneWayInId) return;
    addMember
      .mutateAsync({
        organizationNumber: selectedCompany?.organizationNumber,
        oneWayInId: oneWayInOpportunityById.data?.oneWayInId,
      })
      .then(() => {
        if (feedbackMissing)
          upsertFeedback.mutateAsync(
            createAutomaticFeedbackUpsertDto(
              chosenClusterId,
              oneWayInOpportunityById.data,
              opportunityFeedbackCollection,
            ),
          );
        toast(
          <ToastContent title={tOneWayIn('connectCompany.toast.success.title')} text="" success={true} />,
          {
            toastId: 'CompanyConnectedSuccess',
            autoClose: false,
            position: 'bottom-center',
          },
        );
      })
      .catch(() => {
        toast(
          <ToastContent
            title={tOneWayIn('connectCompany.toast.error.title')}
            text={tOneWayIn('connectCompany.toast.error.text')}
            success={false}
          />,
          {
            toastId: 'CompanyConnectedError',
            autoClose: false,
            position: 'bottom-center',
          },
        );
      })
      .finally(() => onClose());
  };

  return (
    <DialogContainer
      show={show}
      onClose={onClose}
      title={tOneWayIn('connectCompany')}
      className="overflow--visible"
      initialFocusRef={inputRef}
    >
      <div className="stack">
        <p>{tOneWayIn('connectCompany.modal.description.part1')}</p>
        <p>{tOneWayIn('connectCompany.modal.description.part2')}</p>
        <div className="search">
          <BrregCompanySearch
            label={tSiva('newCompany.brregSearch.search.label')}
            inputRef={inputRef}
            onSelect={setSelectedCompany}
            onClearSelection={() => setSelectedCompany(undefined)}
          />
          {selectedCompany && (
            <div className={css['organization-container']}>
              <span className="color--in-black-400">{selectedCompany.name}</span>
              <Button
                type="button"
                icon
                variant="tertiary"
                aria-label={`${tCommon('remove')} ${selectedCompany.name}`}
                onClick={() => setSelectedCompany(undefined)}
              >
                <Icon prefix="fasr" iconName="times" size="l" />
              </Button>
            </div>
          )}
        </div>
        <DialogActions>
          <Button
            type="button"
            onClick={() => onConnectCompany()}
            loading={addMember.isLoading}
            disabled={!selectedCompany || addMember.isLoading}
          >
            {tOneWayIn('connectCompany')}
          </Button>
          <Button type="button" onClick={onClose} variant="secondary">
            {tCommon('cancel')}
          </Button>
        </DialogActions>
      </div>
    </DialogContainer>
  );
};

export default ConnectCompanyModal;
